<ng-container *transloco="let t">
  <div class="page-with-padding">
    <page-wrapper
      [title]="t('carrier_configurations_title')"
      [withMarginTop]="true"
      [topRightButtonLabel]="t('carrier_configurations_add')"
      [topRightButtonClick]="openCreateDrawer.bind(this)"
    >
      <app-search-wrapper
        [searchModel]="getSearchModelByType.bind(this)()"
        [initialSearchValue]="initialSearchValue"
        [filterTags]="[]"
      >
      </app-search-wrapper>

      <carrier-configurations-table
        [tableData]="tableData"
        [tableColumns]="tableColumns"
        [isLoading]="getConfigurationsLoading"
        [versionCountByCarrier]="versionCountByCarrier"
        (rowClicked)="onRowClicked($event)"
        (editClicked)="onEditClicked($event)"
        (activateClicked)="onActivateClicked($event)"
        (switchActiveVersionClicked)="onSwitchActiveVersionClicked($event)"
        (testClicked)="onTestClicked($event)"
        (trackingClicked)="onTrackingClicked($event)"
        (deleteClicked)="onDeleteClicked($event)"
      >
        @if (!getConfigurationsLoading && tableData.length === 0) {
          <div class="table-empty-state" slot="state">
            <p class="table-empty-state-subcontent">
              {{ t("carrier_config_empty_state") }}
            </p>
          </div>
        }
      </carrier-configurations-table>

      @if (!getConfigurationsLoading && tableData.length > 0) {
        <pagination
          [totalPages]="totalPages"
          [currentPage]="currentPage"
          [visiblePages]="6"
          [totalItems]="totalCount"
          [itemsPerPage]="pageSize"
          [pageChanged]="pageChanged.bind(this)"
        ></pagination>
      }
    </page-wrapper>

    <app-dialog
      [open]="isConfirmModalOpen"
      [dialogHeight]="confirmModalMode === 'save' ? '340px' : '260px'"
      [dialogWidth]="'540px'"
      [primaryButtonDisabled]="isSaveConfirmationDisabled()"
      [primaryButtonLabel]="
        confirmModalMode === 'delete' ? t('yes_delete') : t('save_changes')
      "
      [secondaryButtonLabel]="t('discard')"
      [title]="
        confirmModalMode === 'delete'
          ? t('carrier_config_delete_title')
          : 'Confirm save'
      "
      [onPrimaryButtonClick]="onConfirmModalPrimary.bind(this)"
      [onSecondaryButtonClick]="onConfirmModalSecondary.bind(this)"
      [closeOnPrimaryAction]="false"
      [closeOnSecondaryAction]="false"
    >
      @if (isConfirmModalOpen && confirmModalMode === "delete" && currentRow) {
        <div class="carrier-config-delete-content">
          <p>{{ t("carrier_config_delete_subtitle") }}</p>
          <p>
            <strong>{{ currentRow.carrierCode }}</strong> - v{{
              getVersionNumberForListRow(currentRow)
            }}
          </p>
        </div>
      }
      @if (isConfirmModalOpen && confirmModalMode === "save") {
        <div class="carrier-config-delete-content">
          <p>Saving will create a new inactive version.</p>
          <p>You can test it and activate it later.</p>
          <mc-textarea
            [label]="t('carrier_config_description_label')"
            [value]="saveDescriptionControl.value"
            [maxlength]="saveDescriptionMaxLength"
            [rows]="3"
            (input)="onSaveDescriptionInput($event)"
          ></mc-textarea>
        </div>
      }
    </app-dialog>

    <app-dialog
      [open]="isSwitchVersionModalOpen"
      [dialogHeight]="'320px'"
      [dialogWidth]="'540px'"
      [primaryButtonDisabled]="!selectedSwitchVersionId"
      [primaryButtonLabel]="t('carrier_integrations_switch_version_confirm')"
      [secondaryButtonLabel]="t('discard')"
      [title]="t('carrier_integrations_switch_version_title')"
      [onPrimaryButtonClick]="onSwitchVersionConfirm.bind(this)"
      [onSecondaryButtonClick]="onSwitchVersionCancel.bind(this)"
      [closeOnPrimaryAction]="false"
      [closeOnSecondaryAction]="false"
    >
      @if (isSwitchVersionModalOpen) {
        <div class="carrier-config-delete-content">
          <p>{{ t("carrier_integrations_switch_version_subtitle") }}</p>
          <mc-select
            [label]="t('carrier_integrations_switch_version_select_label')"
            [value]="selectedSwitchVersionId?.toString() ?? ''"
            (optionselected)="onSwitchVersionSelect($event)"
          >
            @for (v of switchVersionVersions; track v.id) {
              <mc-option [value]="v.id.toString()">
                v{{ v.version }}
                {{
                  v.isActive
                    ? "(" + t("active") + ")"
                    : "(" + t("inactive") + ")"
                }}
              </mc-option>
            }
          </mc-select>
        </div>
      }
    </app-dialog>

    <side-panel
      extraCssClasses="carrier-config-editor-side-panel"
      [title]="
        isReadOnlyMode
          ? t('carrier_integrations_view_title')
          : isEditMode
            ? t('carrier_config_edit_title')
            : t('carrier_config_add_title')
      "
      [primaryButtonLabel]="getPrimaryActionLabel()"
      [secondaryButtonLabel]="t('discard')"
      [onPrimaryButtonClick]="onSaveConfiguration.bind(this)"
      [onSecondaryButtonClick]="onEditorCancel.bind(this)"
      [primaryButtonDisabled]="isReadOnlyMode || editorDetailLoading"
      [closeOnPrimaryAction]="false"
      [withFooter]="!isReadOnlyMode"
    >
      <div class="carrier-config-editor-shell">
        @if (editorDetailLoading) {
          <div
            class="carrier-config-editor-loading"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <mc-loading-indicator></mc-loading-indicator>
          </div>
        }
        <form class="carrier-config-form">
          <div class="sections-divider mds-neutral--weak__border-color"></div>
          <div class="carrier-config-phase-grid">
            <mc-select
              label="{{ t('carrier_config_phase_label') }}"
              [value]="selectedPhase"
              (optionselected)="onPhaseSelect($event)"
            >
              <mc-option value="orchestration">{{
                getPhaseOptionLabel("orchestration")
              }}</mc-option>
              <mc-option
                value="auth"
                [disabled]="!canEditMappings() || !isPhaseEnabled('auth')"
                >{{ getPhaseOptionLabel("auth") }}</mc-option
              >
              <mc-option
                value="label"
                [disabled]="!canEditMappings() || !isPhaseEnabled('label')"
                >{{ getPhaseOptionLabel("label") }}</mc-option
              >
              <mc-option
                value="tracking"
                [disabled]="!canEditMappings() || !isPhaseEnabled('tracking')"
                >{{ getPhaseOptionLabel("tracking") }}</mc-option
              >
              <mc-option
                value="pudo"
                [disabled]="!canEditMappings() || !isPhaseEnabled('pudo')"
                >{{ getPhaseOptionLabel("pudo") }}</mc-option
              >
            </mc-select>
          </div>

          @if (selectedPhase === "orchestration") {
            <div class="carrier-config-form-grid">
              @if (isEditMode && editorVersions.length > 0) {
                <mc-select
                  label="Version"
                  [value]="selectedEditorVersionId?.toString() ?? ''"
                  (optionselected)="onEditorVersionSelect($event)"
                  [disabled]="isEditMode && !isReadOnlyMode"
                >
                  @for (versionItem of editorVersions; track versionItem.id) {
                    <mc-option [value]="versionItem.id.toString()">
                      v{{ versionItem.version }}
                      {{ versionItem.isActive ? "(active)" : "(inactive)" }}
                    </mc-option>
                  }
                </mc-select>
              }

              @if (!isEditMode && !isReadOnlyMode) {
                <div class="carrier-picker">
                  <mc-typeahead
                    [data]="this.carrierPickerOptionsData"
                    (search)="onCarrierSearchInput($event)"
                    (optionselected)="onCarrierSelected($event)"
                    (clearbuttonclick)="onCarrierDeselected()"
                    label="{{ t('carrier') }}"
                    [placeholder]="t('carrier_picker_search_placeholder')"
                    [keepclearbuttonvisible]="true"
                    [clearbutton]="true"
                  >
                  </mc-typeahead>
                </div>
              } @else {
                <mc-input
                  label="{{ t('carrier') }}"
                  [value]="editorForm.controls.carrierCode.value"
                  [disabled]="true"
                ></mc-input>
              }

              <mc-select
                label="{{ t('status') }}"
                [value]="editorForm.controls.isActive.value"
                (optionselected)="onStatusSelect($event)"
                [disabled]="isEditMode"
              >
                <mc-option value="active">{{ t("active") }}</mc-option>
                <mc-option value="inactive">{{ t("inactive") }}</mc-option>
              </mc-select>
            </div>

            @if (isReadOnlyMode) {
              <mc-textarea
                [rows]="3"
                [label]="t('carrier_config_description_label')"
                [value]="selectedConfiguration?.description ?? ''"
                [disabled]="true"
              ></mc-textarea>
              <label class="carrier-config-json-label">{{
                t("carrier_config_json_label")
              }}</label>
              <textarea
                rows="26"
                class="carrier-config-json-editor-native"
                [value]="editorForm.controls.configurationJson.value"
                readonly
              ></textarea>
            } @else {
              <mc-textarea
                [rows]="26"
                class="carrier-config-json-editor"
                [label]="t('carrier_config_json_label')"
                [value]="editorForm.controls.configurationJson.value"
                (input)="onConfigurationJsonInput($event)"
              ></mc-textarea>
            }
          } @else {
            <div class="carrier-config-form-grid">
              <mc-select
                label="{{ t('carrier_config_mapping_key') }}"
                [value]="mappingForm.controls.mappingKey.value"
                (optionselected)="onMappingKeySelect($event)"
                [disabled]="
                  !canEditMappings() ||
                  getCurrentPhaseMappingKeys().length === 0
                "
              >
                @for (
                  mappingKey of getCurrentPhaseMappingKeys();
                  track mappingKey
                ) {
                  <mc-option [value]="mappingKey">{{ mappingKey }}</mc-option>
                }
              </mc-select>
            </div>

            <p class="carrier-config-phase-helper">
              {{ t("carrier_config_phase_help") }}
            </p>
            @if (hasUnsavedPhaseDrafts()) {
              <p class="carrier-config-phase-helper">
                Unsaved draft changes in this phase.
              </p>
            }

            @if (isReadOnlyMode) {
              <label class="carrier-config-json-label">{{
                t("carrier_config_mapping_json_label")
              }}</label>
              <textarea
                rows="26"
                class="carrier-config-json-editor-native"
                [value]="mappingForm.controls.mappingJson.value"
                readonly
              ></textarea>
            } @else {
              <mc-textarea
                [rows]="26"
                class="carrier-config-json-editor"
                [label]="t('carrier_config_mapping_json_label')"
                [value]="mappingForm.controls.mappingJson.value"
                (input)="onMappingJsonInput($event)"
                [disabled]="!canEditMappings()"
              ></mc-textarea>
            }
          }
        </form>
      </div>
    </side-panel>

    <side-panel
      extraCssClasses="carrier-config-test-side-panel"
      [title]="'Test your integration'"
      [primaryButtonLabel]="testInProgress ? 'Running...' : 'Run test'"
      [secondaryButtonLabel]="'Close'"
      [onPrimaryButtonClick]="onRunIntegrationTest.bind(this)"
      [onSecondaryButtonClick]="closeTestPanel.bind(this)"
      [primaryButtonDisabled]="testInProgress || !selectedTestConfigurationId"
      [closeOnPrimaryAction]="false"
    >
      <form class="carrier-config-form">
        <div class="sections-divider mds-neutral--weak__border-color"></div>
        <div class="carrier-config-phase-grid">
          <mc-select
            label="Version"
            [value]="selectedTestConfigurationId?.toString() ?? ''"
            (optionselected)="onTestVersionSelect($event)"
            [disabled]="testVersions.length === 0"
          >
            @for (versionItem of testVersions; track versionItem.id) {
              <mc-option [value]="versionItem.id.toString()">
                v{{ versionItem.version }}
                {{ versionItem.isActive ? "(active)" : "(inactive)" }}
              </mc-option>
            }
          </mc-select>
        </div>
        <div class="carrier-config-phase-grid">
          <mc-select
            [label]="t('carrier_integrations_flow_label')"
            [value]="testFlowName"
            (optionselected)="onTestFlowSelect($event)"
          >
            @for (opt of testFlowOptions; track opt.value) {
              <mc-option [value]="opt.value">{{ t(opt.labelKey) }}</mc-option>
            }
          </mc-select>
        </div>

        <mc-textarea
          [rows]="12"
          class="carrier-config-json-editor"
          label="Request payload JSON"
          [value]="testRequestJson"
          (input)="onTestRequestInput($event)"
        ></mc-textarea>

        <label class="carrier-config-json-label">Response (read-only)</label>
        <textarea
          rows="12"
          class="carrier-config-json-editor-native"
          [value]="testResponseJson"
          readonly
        ></textarea>
        <p class="carrier-config-phase-helper" *ngIf="testResponseStatus">
          Status: {{ testResponseStatus }} ({{ getTestDurationText() }})
        </p>
      </form>
    </side-panel>

    <side-panel
      extraCssClasses="carrier-tracking-side-panel"
      [title]="trackingSidePanelTitle"
      [withFooter]="false"
      [nopadding]="false"
    >
      <div class="carrier-tracking-panel">
        @if (trackingPanelView === "list") {
          <app-search-wrapper
            [searchModel]="getTrackingSearchModel.bind(this)()"
            [initialSearchValue]="trackingSearchInitialValue"
            [filterTags]="[]"
          ></app-search-wrapper>
          <mc-table
            fit="small"
            [data]="trackingTargets"
            [columns]="trackingListColumns"
            [disablerowhighlightonhover]="
              trackingListLoading || trackingTargets.length === 0
            "
          >
            @if (trackingListLoading) {
              <mc-loading-indicator slot="state"></mc-loading-indicator>
            } @else if (trackingTargets.length === 0) {
              <div class="table-empty-state" slot="state">
                <p class="table-empty-state-subcontent">
                  {{ t("carrier_integrations_tracking_empty") }}
                </p>
              </div>
            } @else {
              @for (item of trackingTargets; track item.id) {
                <span slot="{{ item.id }}_identityFields">{{
                  item.identityFields.length
                    ? formatIdentityFields(item.identityFields)
                    : "—"
                }}</span>
                <span slot="{{ item.id }}_createdAtUtc">{{
                  formatTrackingUtc(item.createdAtUtc)
                }}</span>
                <span slot="{{ item.id }}_lastPolledUtc">{{
                  item.lastPolledUtc
                    ? formatTrackingUtc(item.lastPolledUtc)
                    : "—"
                }}</span>
                <span slot="{{ item.id }}_flags">
                  @if (item.isTerminal) {
                    <mc-tag
                      appearance="neutral-weak"
                      [label]="t('carrier_integrations_tracking_terminal')"
                    ></mc-tag>
                  } @else if (item.isPaused) {
                    <mc-tag
                      appearance="neutral-weak"
                      [label]="t('carrier_integrations_tracking_paused')"
                    ></mc-tag>
                  } @else {
                    <mc-tag
                      appearance="neutral-weak"
                      [label]="t('active')"
                    ></mc-tag>
                  }
                </span>
                <span slot="{{ item.id }}_investigate">
                  <mc-button
                    variant="plain"
                    appearance="primary"
                    fit="small"
                    hiddenlabel="{{
                      t('carrier_integrations_tracking_investigate')
                    }}"
                    (click)="onInvestigateTracking($event, item)"
                  >
                    <span slot="icon"
                      ><mc-icon icon="magnifying-glass" size="16"></mc-icon
                    ></span>
                  </mc-button>
                </span>
              }
            }
          </mc-table>
          @if (!trackingListLoading && trackingTargets.length > 0) {
            <pagination
              [totalPages]="trackingTotalPages"
              [currentPage]="trackingCurrentPage"
              [visiblePages]="6"
              [totalItems]="trackingTotalCount"
              [itemsPerPage]="trackingPageSize"
              [pageChanged]="onTrackingPageChanged.bind(this)"
            ></pagination>
          }
        } @else {
          <div class="carrier-tracking-detail-toolbar">
            <mc-button
              variant="plain"
              appearance="neutral"
              fit="medium"
              (click)="onTrackingBack($event)"
              icon="chevron-left"
            >
              {{ t("carrier_integrations_tracking_back") }}
            </mc-button>
          </div>
          <mc-table
            fit="small"
            [data]="trackingEvents"
            [columns]="trackingEventColumns"
            [disablerowhighlightonhover]="
              trackingEventsLoading || trackingEvents.length === 0
            "
          >
            @if (trackingEventsLoading) {
              <mc-loading-indicator slot="state"></mc-loading-indicator>
            } @else if (trackingEvents.length === 0) {
              <div class="table-empty-state" slot="state">
                <p class="table-empty-state-subcontent">
                  {{ t("carrier_integrations_tracking_events_empty") }}
                </p>
              </div>
            } @else {
              @for (ev of trackingEvents; track ev.outboxId) {
                <span slot="{{ ev.outboxId }}_observedAtUtc">{{
                  formatTrackingUtc(ev.observedAtUtc)
                }}</span>
                <span slot="{{ ev.outboxId }}_status">{{
                  ev.status ?? "—"
                }}</span>
                <span slot="{{ ev.outboxId }}_description">{{
                  ev.description ?? "—"
                }}</span>
                <span slot="{{ ev.outboxId }}_location">{{
                  ev.location ?? "—"
                }}</span>
                <span slot="{{ ev.outboxId }}_carrierEventUtc">{{
                  ev.carrierEventUtc
                    ? formatTrackingUtc(ev.carrierEventUtc)
                    : "—"
                }}</span>
              }
            }
          </mc-table>
        }
      </div>
    </side-panel>
  </div>
</ng-container>
